import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Loader2,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Download,
  MessageCircleQuestion,
  Users,
} from 'lucide-react';
import {
  fetchInsights,
  fetchOrgOverview,
  fetchPulseDaily,
  fetchOrgConversationStats,
  fetchPulseByBand,
  fetchOrgAnchorDaily,
} from '@/lib/admin/admin-api';
import { getOrgBiggestMovers, type OrgMover } from '@/lib/admin/insights-api';
import { ErrorNotice } from '@/components/common/ErrorNotice';
import { AskBriefDialog } from '@/components/admin/insights/AskBriefDialog';

type Valence = 'all' | 'protect' | 'improve';

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  trend,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  trend?: { dir: 'up' | 'down' | 'flat'; label: string } | null;
  tone?: 'default' | 'risk' | 'strength';
}) {
  const TrendIcon = trend?.dir === 'up' ? TrendingUp : trend?.dir === 'down' ? TrendingDown : Minus;
  const trendColor =
    trend?.dir === 'up'
      ? 'text-emerald-600 dark:text-emerald-400'
      : trend?.dir === 'down'
        ? 'text-destructive'
        : 'text-muted-foreground';
  const iconBg =
    tone === 'risk'
      ? 'bg-destructive/10 text-destructive'
      : tone === 'strength'
        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
        : 'bg-primary/10 text-primary';
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="mt-2 text-3xl font-heading font-semibold tabular-nums truncate">{value}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
          </div>
          <div className={`rounded-md p-2 ${iconBg}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        {trend && (
          <div className={`mt-3 flex items-center gap-1 text-xs ${trendColor}`}>
            <TrendIcon className="h-3.5 w-3.5" />
            <span>{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DIMENSIONS = [
  { key: 'department', label: 'Department' },
  { key: 'team', label: 'Team' },
  { key: 'location', label: 'Location' },
  { key: 'level', label: 'Level' },
  { key: 'tenure_years', label: 'Tenure' },
] as const;
type Dim = (typeof DIMENSIONS)[number]['key'];

function meanColor(v: number, min = 1, max = 10) {
  const t = Math.max(0, Math.min(1, (v - min) / (max - min)));
  const hue = 0 + t * 130;
  return `hsl(${hue} 65% 45%)`;
}


export default function Insights() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();
  const [val, setVal] = useState<Valence>('all');
  const [dim, setDim] = useState<Dim>('department');
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefQuestion, setBriefQuestion] = useState<string | undefined>();

  const overviewQ = useQuery({
    queryKey: ['admin', 'overview', orgId],
    queryFn: () => fetchOrgOverview(orgId),
  });

  const pulseDailyQ = useQuery({
    queryKey: ['admin', 'pulse-daily', orgId],
    queryFn: () => fetchPulseDaily(orgId),
  });

  const convStatsQ = useQuery({
    queryKey: ['admin', 'conv-stats', orgId],
    queryFn: () => fetchOrgConversationStats(orgId),
  });

  const insightsQ = useQuery({
    queryKey: ['admin', 'insights', orgId, val],
    queryFn: () => fetchInsights(orgId, val === 'all' ? {} : { valence: val }),
  });

  const { from30, today } = useMemo(() => {
    const t = new Date();
    const f = new Date();
    f.setDate(f.getDate() - 30);
    return { from30: f.toISOString().slice(0, 10), today: t.toISOString().slice(0, 10) };
  }, []);

  const byBandQ = useQuery({
    queryKey: ['admin', 'pulse-by-band', orgId, dim, from30, today],
    queryFn: () => fetchPulseByBand(orgId, dim, from30, today),
  });

  const teamByBandQ = useQuery({
    queryKey: ['admin', 'pulse-by-band', orgId, 'team', from30, today],
    queryFn: () => fetchPulseByBand(orgId, 'team', from30, today),
  });

  const anchorDailyQ = useQuery({
    queryKey: ['admin', 'anchor-daily', orgId, from30, today],
    queryFn: () => fetchOrgAnchorDaily(orgId, from30, today),
  });

  const moversQ = useQuery({
    queryKey: ['admin', 'org-movers', orgId, 30],
    queryFn: () => getOrgBiggestMovers(orgId, 30),
  });

  const pulseSeries = useMemo(() => {
    const rows = pulseDailyQ.data ?? [];
    return [...rows]
      .sort((a, b) => String(a.day).localeCompare(String(b.day)))
      .map((r) => ({ day: String(r.day).slice(5), avg: Number(r.avg_pulse), n: r.n_employees }));
  }, [pulseDailyQ.data]);

  const pulseTrend = useMemo(() => {
    if (pulseSeries.length < 4) return null;
    const half = Math.floor(pulseSeries.length / 2);
    const first = pulseSeries.slice(0, half);
    const last = pulseSeries.slice(half);
    const avg = (xs: typeof pulseSeries) => xs.reduce((s, x) => s + x.avg, 0) / xs.length;
    const delta = avg(last) - avg(first);
    if (Math.abs(delta) < 0.05) return { dir: 'flat' as const, label: 'Stable vs. prior period' };
    return {
      dir: delta > 0 ? ('up' as const) : ('down' as const),
      label: `${delta > 0 ? '+' : ''}${delta.toFixed(2)} vs. prior period`,
    };
  }, [pulseSeries]);

  const aggregateBands = (rows: typeof byBandQ.data) => {
    const acc = new Map<string, { label: string; sum: number; n: number }>();
    for (const r of rows ?? []) {
      const label = r.band_label ?? '—';
      const key = r.band_id ?? label;
      const cur = acc.get(key) ?? { label, sum: 0, n: 0 };
      if (r.avg_pulse != null && r.n_employees != null) {
        cur.sum += Number(r.avg_pulse) * Number(r.n_employees);
        cur.n += Number(r.n_employees);
      }
      acc.set(key, cur);
    }
    return Array.from(acc.values())
      .filter((b) => b.n > 0)
      .map((b) => ({ label: b.label, mean: +(b.sum / b.n).toFixed(2), n: b.n }));
  };

  const bandBars = useMemo(
    () => aggregateBands(byBandQ.data).sort((a, b) => b.mean - a.mean),
    [byBandQ.data],
  );

  const teamBars = useMemo(
    () => aggregateBands(teamByBandQ.data).sort((a, b) => a.mean - b.mean),
    [teamByBandQ.data],
  );

  const largestGap = useMemo(() => {
    if (bandBars.length < 2) return null;
    const hi = bandBars[0];
    const lo = bandBars[bandBars.length - 1];
    return { hi, lo, gap: +(hi.mean - lo.mean).toFixed(2) };
  }, [bandBars]);

  const mostAtRiskTeam = teamBars[0] ?? null;

  const anchorSummary = useMemo(() => {
    const rows = anchorDailyQ.data ?? [];
    const acc = new Map<string, { name: string; sum: number; n: number }>();
    for (const r of rows) {
      const cur = acc.get(r.anchor_id) ?? { name: r.anchor_name, sum: 0, n: 0 };
      cur.sum += Number(r.mean_value) * Number(r.n);
      cur.n += Number(r.n);
      acc.set(r.anchor_id, cur);
    }
    return Array.from(acc.values())
      .filter((a) => a.n > 0)
      .map((a) => ({ name: a.name, mean: +(a.sum / a.n).toFixed(2), n: a.n }))
      .sort((a, b) => b.mean - a.mean);
  }, [anchorDailyQ.data]);

  // Detect scale from observed max mean; treat >5 as 1-10 scale.
  const anchorScaleMax = useMemo(
    () => (anchorSummary.some((a) => a.mean > 5) ? 10 : 5),
    [anchorSummary],
  );




  const notableRisk = anchorSummary.length ? anchorSummary[anchorSummary.length - 1] : null;
  const notableStrength = anchorSummary.length ? anchorSummary[0] : null;

  const movers = useMemo<OrgMover[]>(() => {
    const rows = moversQ.data ?? [];
    // Order by |delta| desc, drop nulls, keep top items.
    return [...rows]
      .filter((r) => r.delta != null)
      .sort((a, b) => Math.abs(Number(b.delta ?? 0)) - Math.abs(Number(a.delta ?? 0)));
  }, [moversQ.data]);

  // (mover deltas are looked up by name inline in the anchor-health block)

  const overview = overviewQ.data;
  const conv = convStatsQ.data;
  const publishedCount = insightsQ.data?.length ?? 0;

  const participationPct =
    overview && overview.employee_count > 0 && conv && !conv.suppressed
      ? Math.round((conv.active_employee_count / overview.employee_count) * 100)
      : null;

  const openInAsk = (question: string) => {
    navigate(`/app/admin/insights/ask?q=${encodeURIComponent(question)}`);
  };

  const openBriefFor = (question?: string) => {
    setBriefQuestion(question);
    setBriefOpen(true);
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Culture insights</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Track what is shifting across pulse, participation, and anchor health.
            Use <span className="font-medium">Ask</span> for deeper narrative analysis.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/app/admin/insights/ask')}>
            <MessageCircleQuestion className="h-4 w-4 mr-2" />
            Open Ask
          </Button>
          <Button size="sm" onClick={() => openBriefFor()}>
            <Download className="h-4 w-4 mr-2" />
            Download Ask brief
          </Button>
        </div>
      </div>

      {/* Block 1 — Executive summary */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={Activity}
          label="Pulse (last 7d)"
          value={
            overviewQ.isLoading
              ? '—'
              : overview?.last_7d_suppressed || overview?.last_7d_org_avg == null
                ? 'k<5'
                : Number(overview.last_7d_org_avg).toFixed(2)
          }
          hint={
            overview?.last_7d_suppressed
              ? 'Not enough responses — hidden'
              : overview?.last_7d_n != null
                ? `n=${overview.last_7d_n} responses`
                : undefined
          }
          trend={pulseTrend}
        />
        <KpiCard
          icon={Users}
          label="Participation & coverage"
          value={
            overviewQ.isLoading
              ? '—'
              : participationPct != null
                ? `${participationPct}%`
                : String(overview?.employee_count ?? 0)
          }
          hint={
            overview
              ? `${overview.employee_count} people · ${conv && !conv.suppressed ? `${conv.total_conversations} conversations` : 'conversations hidden'}`
              : undefined
          }
        />
        <KpiCard
          icon={ShieldAlert}
          label="Notable risk"
          tone="risk"
          value={notableRisk ? notableRisk.mean.toFixed(2) : '—'}
          hint={
            notableRisk
              ? `${notableRisk.name} · n=${notableRisk.n}`
              : 'No anchor cleared k≥5'
          }
        />
        <KpiCard
          icon={ShieldCheck}
          label="Notable strength"
          tone="strength"
          value={notableStrength ? notableStrength.mean.toFixed(2) : '—'}
          hint={
            notableStrength
              ? `${notableStrength.name} · n=${notableStrength.n}`
              : 'No anchor cleared k≥5'
          }
        />
      </div>

      {/* Pulse trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Org pulse trend</CardTitle>
          <CardDescription>Daily average pulse. Suppressed days are excluded.</CardDescription>
        </CardHeader>
        <CardContent>
          {pulseDailyQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : pulseSeries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pulse data yet.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pulseSeries}>
                  <defs>
                    <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis domain={[1, 10]} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="avg"
                    name="Avg pulse"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#pulseFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block 2 — What changed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">What changed this period</CardTitle>
          <CardDescription>
            Biggest anchor shifts, 30 days vs prior 30. Only cells with k≥5 in both windows are shown.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {moversQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : movers.length > 0 ? (
            movers.slice(0, 3).map((m) => {
              const up = (m.delta ?? 0) >= 0;
              return (
                <div
                  key={`${m.anchor_id}-${m.survey_id ?? ''}`}
                  className="border border-border rounded-md p-3 flex items-center justify-between bg-card"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{m.anchor_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.survey_name ? <>{m.survey_name} · </> : null}
                      now {m.current_mean?.toFixed?.(2) ?? '–'} · prior {m.prior_mean?.toFixed?.(2) ?? '–'} ·
                      n {m.n_current}/{m.n_prior}
                    </div>
                  </div>
                  <Badge variant={up ? 'default' : 'destructive'} className="shrink-0">
                    {up ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                    {up ? '+' : ''}
                    {m.delta?.toFixed?.(2) ?? '0.00'}
                  </Badge>
                </div>
              );
            })
          ) : notableRisk ? (
            <p className="text-sm text-muted-foreground">
              No anchor shifts cleared visibility thresholds this period. Lowest visible anchor
              remains <span className="font-medium text-foreground">{notableRisk.name}</span> at{' '}
              <span className="tabular-nums">{notableRisk.mean.toFixed(2)}</span> across n={notableRisk.n}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Not enough data yet to detect shifts under the k≥5 floor.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Block 3 — Segment hotspots */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base font-heading">Where it's happening</CardTitle>
            <CardDescription>Segment comparison over the last 30 days.</CardDescription>
          </div>
          <Select value={dim} onValueChange={(v) => setDim(v as Dim)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIMENSIONS.map((d) => (
                <SelectItem key={d.key} value={d.key}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-3">
          {largestGap && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
              <div>
                <span className="text-muted-foreground">Largest gap: </span>
                <span className="font-medium tabular-nums">{largestGap.gap}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Highest: </span>
                <span className="font-medium">{largestGap.hi.label}</span>{' '}
                <span className="tabular-nums text-muted-foreground">
                  ({largestGap.hi.mean.toFixed(2)}, n={largestGap.hi.n})
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Lowest: </span>
                <span className="font-medium">{largestGap.lo.label}</span>{' '}
                <span className="tabular-nums text-muted-foreground">
                  ({largestGap.lo.mean.toFixed(2)}, n={largestGap.lo.n})
                </span>
              </div>
            </div>
          )}
          {byBandQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : bandBars.length === 0 ? (
            <p className="text-sm text-muted-foreground">No visible bands under the k≥5 floor.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bandBars} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
                  <XAxis type="number" domain={[1, 10]} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => v.toFixed(2)} />
                  <Bar dataKey="mean" radius={[0, 4, 4, 0]}>
                    {bandBars.map((b, i) => (
                      <Cell key={i} fill={meanColor(b.mean)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {mostAtRiskTeam && (
            <p className="text-sm">
              <span className="text-muted-foreground">Most-at-risk team: </span>
              <span className="font-medium">{mostAtRiskTeam.label}</span>{' '}
              <span className="tabular-nums text-muted-foreground">
                ({mostAtRiskTeam.mean.toFixed(2)}, n={mostAtRiskTeam.n})
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Block 4 — Current anchor scores (flat list; no Protect/Watch/Improve buckets here
          to avoid classification conflict with the qualitative Leader actions below). */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Current anchor scores</CardTitle>
          <CardDescription>
            Measured 30-day culture scores across active surveys, sorted strongest to weakest.
            Score-based ranking only — recommended actions live in Leader actions below and can
            differ from raw scores.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {anchorDailyQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : anchorSummary.length === 0 ? (
            <p className="text-sm text-muted-foreground">No anchor responses cleared the k≥5 floor.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {anchorSummary.map((a) => {
                const anchorDelta =
                  movers.find((m) => m.anchor_name === a.name)?.delta ?? null;
                const dir =
                  anchorDelta == null
                    ? 'flat'
                    : anchorDelta > 0.05
                      ? 'up'
                      : anchorDelta < -0.05
                        ? 'down'
                        : 'flat';
                const DirIcon =
                  dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
                const dirColor =
                  dir === 'up'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : dir === 'down'
                      ? 'text-destructive'
                      : 'text-muted-foreground';
                return (
                  <div
                    key={a.name}
                    className="rounded-md border border-border p-2.5 flex items-center justify-between gap-2 bg-card"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{a.name}</div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">
                        {a.mean.toFixed(2)} / {anchorScaleMax} · n={a.n}
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${dirColor}`}>
                      <DirIcon className="h-3.5 w-3.5" />
                      {anchorDelta != null && (
                        <span className="tabular-nums">
                          {anchorDelta > 0 ? '+' : ''}
                          {anchorDelta.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block 5 — Leader actions (qualitative findings; explicit action taxonomy to avoid
          collision with the score-based ranking above). */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base font-heading">Leader actions</CardTitle>
            <CardDescription>
              Recommended actions derived from qualitative conversation themes (k-anonymized at
              publish). These come from what people are talking about and can differ from raw
              scores above.
              {publishedCount > 0 && (
                <span className="ml-1 tabular-nums text-muted-foreground">
                  · {publishedCount} published
                </span>
              )}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Tabs value={val} onValueChange={(v) => setVal(v as Valence)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="protect">Protect</TabsTrigger>
              <TabsTrigger value="improve">Improve</TabsTrigger>
            </TabsList>
          </Tabs>

          {insightsQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : insightsQ.error ? (
            <ErrorNotice
              error={insightsQ.error}
              title="Could not load insights"
              onRetry={() => void insightsQ.refetch()}
            />
          ) : insightsQ.data!.length === 0 ? (
            <p className="text-sm text-muted-foreground">No published insights yet.</p>
          ) : (
            <div className="space-y-3">
              {insightsQ.data!.map((i) => {
                const themeQ = i.valence === 'protect'
                  ? `What's making "${i.theme}" a strength right now, and how can leaders keep it that way?`
                  : `What's driving concerns around "${i.theme}", and what should leaders do about it?`;
                const actionVerb = i.valence === 'protect' ? 'Protect' : 'Improve';
                const actionLabel = `Action: ${actionVerb} ${i.theme}`;
                return (
                  <div key={i.id} className="rounded-md border border-border bg-card p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">{i.theme}</div>
                      <Badge variant={i.valence === 'protect' ? 'secondary' : 'default'}>
                        {actionLabel}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {i.what_phrase} · window {i.window_start} → {i.window_end} · k={i.k_at_publish}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openInAsk(themeQ)}
                        className="h-7 text-xs"
                      >
                        <Sparkles className="h-3 w-3 mr-1.5" />
                        Open in Ask
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AskBriefDialog
        open={briefOpen}
        onOpenChange={setBriefOpen}
        orgId={orgId}
        initialQuestion={briefQuestion}
      />
    </div>
  );
}
